import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  MapEntityEditorViewState,
  closeTimingPlaceModalAction,
  selectCopyStopId,
  selectDraftLocation,
  setCopyStopIdAction,
  setDraftLocationAction,
  setDraftVehicleModeAction,
  setMapStopViewStateAction,
  setSelectedStopIdAction,
  useAppAction,
  useAppSelector,
} from '../../../../redux';
import { isDateInRange, parseDate } from '../../../../time';
import { Point } from '../../../../types';
import { showDangerToast, showWarningToast } from '../../../../utils';
import { StopFormState } from '../../../StopRegistry/Stops/Common/StopForm';
import { useGetStopDetailsLazy } from '../../../StopRegistry/Stops/Queries';
import {
  mapStopFormStateToInputs,
  useCopyStop,
} from '../../../StopRegistry/Stops/StopDetails/StopVersion/Utils';
import { useMapUrlStateContext } from '../../Utils/mapUrlState';
import { CreateChanges, StopInfoForEditingOnMap } from '../Types';

function useDefaultValues(
  draftLocation: Point | null,
  stopInfo: StopInfoForEditingOnMap | null,
): Partial<StopFormState> | null {
  return useMemo(() => {
    if (draftLocation && stopInfo) {
      return {
        ...stopInfo.formState,
        ...draftLocation,

        // We need to set these to null to make stopForm think we are creating a new stop
        quayId: undefined,
        stopId: undefined,
      };
    }

    return null;
  }, [draftLocation, stopInfo]);
}

export function useMapCopyStopUtils(
  stopInfo: StopInfoForEditingOnMap | null,
  onCopyFinished: (netexId: string) => void,
  onPopupClose: () => void,
  setDialogOpen: Dispatch<SetStateAction<boolean>>,
) {
  const { t } = useTranslation();

  const {
    state: {
      filters: { observationDate },
    },
    setFlatUrlState,
  } = useMapUrlStateContext();

  const copyStopId = useAppSelector(selectCopyStopId);
  const draftLocation = useAppSelector(selectDraftLocation);

  const dispatch = useDispatch();
  const setSelectedStopId = useAppAction(setSelectedStopIdAction);
  const setCopyStopId = useAppAction(setCopyStopIdAction);
  const setMapStopViewState = useAppAction(setMapStopViewStateAction);
  const setDraftStopLocation = useAppAction(setDraftLocationAction);
  const setDraftVehicleMode = useAppAction(setDraftVehicleModeAction);

  const getStopDetails = useGetStopDetailsLazy();
  const copyStop = useCopyStop();

  const defaultValues = useDefaultValues(draftLocation ?? null, stopInfo);

  const onStartCopyStop = () => {
    if (!copyStopId) {
      return;
    }

    setDialogOpen(false);
    setSelectedStopId(undefined);
    setDraftStopLocation(undefined);
    setDraftVehicleMode(undefined);
    setMapStopViewState(MapEntityEditorViewState.PLACECOPY);
  };

  const onCancelCopyStop = () => {
    setDialogOpen(false);
    setSelectedStopId(copyStopId);
    setCopyStopId(undefined);
    setDraftStopLocation(undefined);
    setDraftVehicleMode(undefined);
  };

  const onCloseCopyModal = () => {
    onCancelCopyStop();

    if (stopInfo) {
      setMapStopViewState(MapEntityEditorViewState.POPUP);
    } else {
      setMapStopViewState(MapEntityEditorViewState.NONE);
      dispatch(closeTimingPlaceModalAction());
      onPopupClose();
    }
  };

  const onCopyStopFormSubmit = async (
    changes: CreateChanges,
    state: StopFormState,
  ) => {
    // There are conflicts, don't copy and warn the user
    if (changes.conflicts?.length) {
      showDangerToast(t(($) => $.stops.stopConflictsOnCopyError));
      return;
    }

    const publicCode = defaultValues?.publicCode?.value;
    const priority = defaultValues?.priority;
    if (!publicCode || !priority) {
      return;
    }

    const { stopDetails } = await getStopDetails(
      publicCode ?? '',
      observationDate,
      priority,
    );

    if (!stopDetails) {
      throw new Error('Stop details not found for copied stop!');
    }

    const { quayInput, stopPointInput, infoSpotInputs, originalStopPlaceId } =
      mapStopFormStateToInputs(state, stopDetails);

    const { quayId } = await copyStop({
      originalStopPlaceId,
      quayInput,
      stopPointInput,
      infoSpotInputs,
    });

    if (
      !isDateInRange(
        observationDate,
        parseDate(state.validityStart),
        parseDate(state.validityEnd),
      )
    ) {
      setFlatUrlState((p) => ({
        ...p,
        observationDate:
          parseDate(state.validityStart) ??
          parseDate(state.validityEnd) ??
          observationDate,
      }));
      showWarningToast(t(($) => $.filters.observationDateAdjusted));
    }

    setCopyStopId(undefined);
    setDialogOpen(false);
    onCopyFinished(quayId);
  };

  return {
    defaultStopFormValues: defaultValues,
    onStartCopyStop,
    onCancelCopyStop,
    onCloseCopyModal,
    onCopyStopFormSubmit,
  };
}
